import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Shield, Home, Car, Briefcase, Heart, HeartPulse, Umbrella, Scale, Clock, Phone, ArrowRight, Star, Users, Award, CheckCircle, Plane, MapPin } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  { icon: Home, title: "Home Insurance", desc: "Protect your biggest investment with comprehensive homeowners coverage.", color: "bg-blue-50", link: "/products" },
  { icon: Car, title: "Auto Insurance", desc: "Tailored auto policies for every driver and vehicle on the road.", color: "bg-amber-50", link: "/products" },
  { icon: Briefcase, title: "Commercial Insurance", desc: "Safeguard your business with customized commercial solutions.", color: "bg-emerald-50", link: "/products" },
  { icon: Heart, title: "Life Insurance", desc: "Secure your family's future with reliable life insurance plans.", color: "bg-rose-50", link: "/products" },
  { icon: HeartPulse, title: "Health Insurance", desc: "Access quality healthcare with comprehensive health coverage.", color: "bg-purple-50", link: "/products" },
  { icon: Umbrella, title: "Annuities", desc: "Build a stable retirement with our annuity investment options.", color: "bg-teal-50", link: "/products" },
  { icon: Scale, title: "LegalShield", desc: "Affordable legal protection — available nationwide.", color: "bg-indigo-50", link: "/products" },
  { icon: Clock, title: "Long-term Care", desc: "Plan ahead with long-term care insurance for peace of mind.", color: "bg-orange-50", link: "/products" },
  { icon: Plane, title: "Travel Insurance", desc: "Travel with confidence — coverage available anywhere in the USA.", color: "bg-sky-50", link: "/products" },
];

const faqs = [
  { q: "What types of insurance do you offer?", a: "We offer a wide range of insurance options including homeowners insurance, auto insurance, commercial insurance, life insurance, health insurance, annuities, LegalShield legal protection, and long-term care insurance." },
  { q: "How do I contact your agency?", a: "You can contact us by phone at (219) 779-7040, emailing customerservice@a2insure.com, or by filling out the form on our contact page. Our friendly staff is ready to assist you." },
  { q: "What sets your agency apart?", a: "As an independent agency, we shop multiple carriers to find you the best coverage at the best price. We provide exceptional customer service, personalized insurance solutions, and in-depth industry knowledge." },
  { q: "Do you offer any discounts?", a: "Yes, we offer various discounts such as multi-policy discounts, safe driver discounts, and loyalty discounts. Contact us to learn more about how you can save." },
  { q: "How can I file a claim?", a: "You can file a claim by calling the claims number on your policy, contacting our team at (219) 779-7040, or emailing us at customerservice@a2insure.com. We'll guide you through the entire process." },
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(r => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section data-testid="hero-section" className="hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-[#D4AF37] blur-[120px]" />
          <div className="absolute bottom-20 left-20 w-72 h-72 rounded-full bg-white blur-[100px]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-up">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8">
                <Award className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-white/80 text-xs font-medium uppercase tracking-wide">Independent Insurance Agency</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 sm:mb-6">
                Protecting What<br />
                <span className="text-[#D4AF37]">Matters Most</span>
              </h1>
              <p className="text-white/70 text-sm sm:text-base lg:text-lg max-w-lg leading-relaxed mb-8 sm:mb-10">
                Anderson & Anderson Insurance LLC provides reliable, personalized insurance solutions for families and businesses across Northwest Indiana, Chicagoland, and five states.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/contact">
                  <Button data-testid="hero-quote-btn" className="w-full sm:w-auto bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold px-6 sm:px-8 py-3 h-auto text-sm sm:text-base rounded-md shadow-lg">
                    Get a Free Quote
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/appointments">
                  <Button data-testid="hero-schedule-btn" variant="outline" className="w-full sm:w-auto border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#002147] px-6 sm:px-8 py-3 h-auto text-sm sm:text-base rounded-md">
                    Schedule Appointment
                  </Button>
                </Link>
              </div>
            </div>

            <div className="animate-slide-right delay-200 hidden lg:block">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1767082091019-a73c4ff55ca4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGZhbWlseSUyMG91dHNpZGUlMjBob21lfGVufDB8fHx8MTc3Mzc5MjM3Mnww&ixlib=rb-4.1.0&q=85&w=800"
                  alt="Happy family outside their home"
                  className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                />
                {/* Floating card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-5 max-w-[220px]">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                      <Users className="h-5 w-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-[#002147] font-bold text-lg">10+</p>
                      <p className="text-slate-500 text-xs">Years Experience</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { num: "10+", label: "Years of Experience" },
              { num: "500+", label: "Happy Clients" },
              { num: "9+", label: "Insurance Products" },
              { num: "5", label: "States Served" },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <p className="font-heading text-2xl sm:text-3xl font-bold text-[#002147]">{s.num}</p>
                <p className="text-slate-500 text-sm mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section data-testid="service-areas-section" className="bg-white py-12 sm:py-16 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">Where We Serve</span>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#002147] mt-2">
              Proudly Serving the Midwest & Beyond
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            {["Indiana", "Illinois", "Michigan", "Ohio", "Florida"].map((state) => (
              <div key={state} className="flex items-center justify-center gap-2 bg-[#F8FAFC] rounded-lg py-3 px-4 border border-slate-100">
                <MapPin className="h-4 w-4 text-[#D4AF37] shrink-0" />
                <span className="text-[#002147] font-medium text-sm">{state}</span>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="bg-[#002147]/5 rounded-lg p-4 text-center">
              <p className="text-[#002147] font-semibold text-sm">LegalShield</p>
              <p className="text-slate-500 text-xs mt-1">Available in states that don't require insurance licensing</p>
            </div>
            <div className="bg-[#002147]/5 rounded-lg p-4 text-center">
              <p className="text-[#002147] font-semibold text-sm">Travel Insurance</p>
              <p className="text-slate-500 text-xs mt-1">Available anywhere in the USA</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="bg-[#F8FAFC] py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">Our Services</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#002147] mt-3 mb-4">
              Comprehensive Coverage Solutions
            </h2>
            <p className="text-slate-500 text-base lg:text-lg max-w-2xl mx-auto">
              As an independent agency, we shop multiple carriers to find the best protection for your unique needs.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {services.map((svc, i) => (
              <Link to={svc.link} key={i}>
                <Card
                  data-testid={`service-card-${svc.title.toLowerCase().replace(/\s/g, '-')}`}
                  className="card-hover bg-white p-4 sm:p-6 rounded-xl border border-slate-100 group cursor-pointer h-full"
                >
                  <CardContent className="p-0">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 ${svc.color} rounded-lg flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}>
                      <svc.icon className="h-5 w-5 sm:h-6 sm:w-6 text-[#002147]" />
                    </div>
                    <h3 className="font-heading text-base sm:text-lg font-semibold text-[#002147] mb-1 sm:mb-2">{svc.title}</h3>
                    <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">{svc.desc}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white py-12 sm:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1696861270495-7f35c35c3273?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxpbnN1cmFuY2UlMjBhZ2VudCUyMGhhbmRzaGFrZSUyMG1lZXRpbmd8ZW58MHx8fHwxNzczNzkyMzc0fDA&ixlib=rb-4.1.0&q=85&w=700"
                alt="Insurance agent meeting with client"
                className="rounded-2xl shadow-lg w-full h-auto object-cover"
              />
            </div>
            <div>
              <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">Why Choose Us</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#002147] mt-3 mb-6">
                Your Trusted Insurance Partner
              </h2>
              <div className="space-y-5">
                {[
                  { title: "Independent Agency", desc: "We shop multiple carriers to find you the best coverage at the best price." },
                  { title: "Personalized Solutions", desc: "Every client gets a customized insurance plan tailored to their unique needs." },
                  { title: "Local Expertise", desc: "Based in Crown Point, IN — we understand the needs of our community." },
                  { title: "Claims Support", desc: "We're with you every step of the way when you need to file a claim." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <CheckCircle className="h-6 w-6 text-[#D4AF37] shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-[#002147] mb-1">{item.title}</h4>
                      <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="bg-[#F8FAFC] py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">Testimonials</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#002147] mt-3">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div
                key={t.id}
                data-testid={`testimonial-card-${i}`}
                className="testimonial-card bg-white p-8 rounded-xl shadow-sm"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, si) => (
                    <Star key={si} className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-slate-600 italic leading-relaxed mb-6 font-heading text-lg">"{t.text}"</p>
                <div>
                  <p className="font-semibold text-[#002147]">{t.name}</p>
                  <p className="text-slate-400 text-sm">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="bg-white py-16 sm:py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">FAQ</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#002147] mt-3">
              Frequently Asked Questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-[#F8FAFC] rounded-xl px-6 border border-slate-100"
              >
                <AccordionTrigger className="text-left font-semibold text-[#002147] hover:no-underline py-5">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-500 leading-relaxed pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="hero-gradient py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Protect What Matters?
          </h2>
          <p className="text-white/70 text-base lg:text-lg mb-8 max-w-xl mx-auto">
            Get a personalized quote from our expert team. We'll find the right coverage at the right price.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button data-testid="cta-quote-btn" className="bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold px-8 py-3 h-auto text-base rounded-md shadow-lg">
                Get a Free Quote
              </Button>
            </Link>
            <a href="tel:2197797040">
              <Button data-testid="cta-call-btn" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-3 h-auto text-base rounded-md">
                <Phone className="h-4 w-4 mr-2" />
                Call (219) 779-7040
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
